import { getProductTypeEnumFromId } from '_components/organisms/Dashboard/Dashboard.types';
import { useAvailableBoostStore } from '_stores/availableBoost/availableBoost';
import { useState } from 'react';
import { DistributionChangeType } from '_components/molecules/CreditDispatchTable/CreditDispatchTable.types';
import { Distribution, useDistributionStore } from '_stores/distribution/distribution';
import { MemberDistributionResponse } from '_clients/BFFClient';
import useMarketStore from '_stores/market';
import useNetworkMarketsData from '../useNetworkMarketsData';

const useCreditDispatch = (backendMembers, marketId, productType, setDistributionChangeInProgress, networkId, contractId) => {

    const { availableBoosts, clearAvailableBoost } = useAvailableBoostStore()
    const { networkMarkets } = useNetworkMarketsData(networkId, contractId)
    const { clearAllMatchingDistributions, getDistributionChangeType, filterDistributions } = useDistributionStore();
    const { setNetworkMarkets } = useMarketStore();
    const [members, setMembers] = useState<MemberDistributionResponse[]>([])

    const compareAndUpdateDistributions = () => {
        const distributionsFromLocalStorage = filterDistributions(marketId, productType, backendMembers, networkId, contractId);
        const distributionsFromLocalStorageCount = distributionsFromLocalStorage?.length ?? 0;
        let distributionChangeType = getDistributionChangeType(marketId, productType, networkId, contractId);

        if (distributionsFromLocalStorageCount > 0) {
            const distributionsFromBackend = backendMembers;
            const canCompare = canCompareDistributions(distributionsFromLocalStorage, distributionsFromBackend, distributionsFromLocalStorageCount);
            // si toutes les distributions contenues dans le localstorage sont présentes dans la réponse du backend alors on peut les comparer et maj le network market store et reset le store des boosts disponibles
            if (canCompare) {
                const { changeTypeInProgress, members: newMembers, isDifferenceFound } = findDistributionsDifferences(distributionsFromLocalStorage, distributionsFromBackend, members);
                distributionChangeType = changeTypeInProgress;
                backendMembers = newMembers;
                if (!isDifferenceFound) {
                    clearAllMatchingDistributions(networkId, contractId, marketId, productType);
                    let updatedNetworkMarkets = [...networkMarkets];
                    let marketToUpdate = updatedNetworkMarkets.find(m => m.id == marketId);
                    if (marketToUpdate) {
                        const productIndexToUpdate = marketToUpdate.products.findIndex(p => productType == getProductTypeEnumFromId(p.id.toString()));
                        if (networkId && productIndexToUpdate != -1) {
                            updateProduct(marketToUpdate, productIndexToUpdate);
                        }
                        else if (contractId && productIndexToUpdate != -1) {
                            updateContract(marketToUpdate, productIndexToUpdate);
                        }
                        const marketIndex = updatedNetworkMarkets.findIndex(m => m.id == marketId);
                        updatedNetworkMarkets[marketIndex] = marketToUpdate;
                    }
                    setNetworkMarkets(updatedNetworkMarkets, networkId, contractId);
                    clearAvailableBoost(networkId, contractId, Number(marketId), productType);
                }
            }
        }

        setMembers(backendMembers)
        setDistributionChangeInProgress(distributionChangeType);
    }

    const updateProduct = (marketToUpdate, productIndexToUpdate) => {
        const productToUpdate = {
            ...marketToUpdate.products[productIndexToUpdate],
            totalAssignedNotUsedBoosts: availableBoosts.find(a => a.networkId == networkId && a.marketId == marketId && a.productType == productType)?.newDistributedNotUsedBoosts ?? marketToUpdate.products[productIndexToUpdate].totalAssignedNotUsedBoosts,
            totalAvailableBoosts: availableBoosts.find(a => a.networkId == networkId && a.marketId == marketId && a.productType == productType)?.newTotalAvailableBoosts ?? marketToUpdate.products[productIndexToUpdate].totalAvailableBoosts,
        };
        marketToUpdate.products[productIndexToUpdate] = productToUpdate;
    }

    const updateContract = (marketToUpdate, productIndexToUpdate) => {
        const contractIndexToUpdate = marketToUpdate.products[productIndexToUpdate].contracts.findIndex(c => c.contractId == contractId);
        if (contractIndexToUpdate != -1) {
            const contractToUpdate = {
                ...marketToUpdate.products[productIndexToUpdate].contracts[contractIndexToUpdate],
                assignedNotUsed: availableBoosts.find(a => a.contractId == contractId && a.marketId == marketId && a.productType == productType)?.newDistributedNotUsedBoosts,
                available: availableBoosts.find(a => a.contractId == contractId && a.marketId == marketId && a.productType == productType)?.newTotalAvailableBoosts,
            }
            marketToUpdate.products[productIndexToUpdate].contracts[contractIndexToUpdate] = contractToUpdate;
        }
    }


    const canCompareDistributions = (distributionsFromLocalStorage: Distribution[], distributionsFromBackend: MemberDistributionResponse[], distributionsFromLocalStorageCount) => {
        return distributionsFromBackend.filter(db => distributionsFromLocalStorage.some(ds => ds.rcuId == db.rcuID)
        ).length == distributionsFromLocalStorageCount;
    }

    const findDistributionsDifferences = (localStorageDistributions: Distribution[], backendDistributions: MemberDistributionResponse[], members) => {
        let changeTypeInProgress = null;
        let isDifferenceFound = false;
        for (let localDist of localStorageDistributions) {
            let backendDist = backendDistributions.find((d) => d.rcuID == localDist.rcuId);
            if (!backendDist) continue;

            const isBoostDifferent = localDist.newAllocatedBoosts >= 0 && localDist.newAllocatedBoosts != backendDist.allocatedBoosts;
            const isModeDifferent = localDist.selectionModeType && localDist.selectionModeType != backendDist.selectionModeType;

            if (isBoostDifferent || isModeDifferent) {
                if (isModeDifferent) {
                    if (changeTypeInProgress != DistributionChangeType.ALL) {
                        changeTypeInProgress = changeTypeInProgress == DistributionChangeType.ALLOCATED_BOOST
                            ? DistributionChangeType.ALL
                            : DistributionChangeType.DISTRIBUTION_MODE;
                    }
                }

                if (isBoostDifferent) {
                    if (changeTypeInProgress != DistributionChangeType.ALL) {
                        changeTypeInProgress = changeTypeInProgress == DistributionChangeType.DISTRIBUTION_MODE
                            ? DistributionChangeType.ALL
                            : DistributionChangeType.ALLOCATED_BOOST;
                    }
                }

                if (changeTypeInProgress != null) {
                    members = updateBackendMembersWithLocalStorage(backendDistributions, localStorageDistributions);
                    isDifferenceFound = true;
                }
            }
        }

        return { changeTypeInProgress, members, isDifferenceFound };
    };

    const updateBackendMembersWithLocalStorage = (backendDistributions: MemberDistributionResponse[], localStorageDistributions: Distribution[]) => {
        return backendDistributions.map(backendDist => {
            const localDist = localStorageDistributions.find(local => local.rcuId == backendDist.rcuID);
            return {
                ...backendDist,
                allocatedBoosts: localDist?.newAllocatedBoosts ?? backendDist.allocatedBoosts,
                selectionModeType: localDist?.selectionModeType ?? backendDist.selectionModeType
            } as MemberDistributionResponse;
        });
    }

    return { compareAndUpdateDistributions, members };
}

export default useCreditDispatch;